import type { ReactNode } from 'react';
import type { Inscription, Side } from '../../engine';
import { useTheme } from '../theme';
import { RouteGlyph } from './RouteGlyph';
import type { PixelSprite } from './night/pixelBroker';
import { SpritePaths } from './night/Sprite';
import {
  ORB,
  ORB_CROWNED,
  SHADOW,
  SHADOW_WIDE,
  SHARD,
  SHARD_CROWNED,
  SHELL,
  SHELL_CROWNED,
  STARFISH,
  STARFISH_CROWNED,
} from './pixelPieces';

/**
 * A piece on the board (Appendix A). Not a chess piece, and the sides always differ by shape as
 * well as color (§19.13). The game is pixel art: in the void, South's pawns are small banded
 * worlds of pale blue light and North's are cut crystal shards in rose, hovering a little above
 * their shadow; a crowned pawn is bigger and wears a tilted ring of gold. In the sea they are
 * scallop shells and starfish, and a crowned one wears a gold crown with a pearl. The sprites are
 * drawn by scripts/art/pixel-pieces.py (pixelPieces.ts).
 *
 * A piece is drawn in layers over the same square: the ground (its shadow) and its body. Only
 * whole layers move (the body floats, the glints of a crowned piece twinkle), so that the browser
 * moves them without redrawing them.
 */

const SPRITES: Readonly<
  Record<'void' | 'sea', Record<Side, readonly [string, PixelSprite, PixelSprite]>>
> = {
  void: { S: ['orb', ORB, ORB_CROWNED], N: ['shard', SHARD, SHARD_CROWNED] },
  sea: { S: ['shell', SHELL, SHELL_CROWNED], N: ['starfish', STARFISH, STARFISH_CROWNED] },
};

/** A pixel sprite as an SVG of its own size; `name` says which, for the tests. */
function SpriteSvg({ sprite, name }: { sprite: PixelSprite; name?: string }) {
  return (
    <svg
      viewBox={`0 0 ${sprite.w} ${sprite.h}`}
      className="pawn-svg"
      shapeRendering="crispEdges"
      data-piece={name}
    >
      <SpritePaths sprite={sprite} />
    </svg>
  );
}

export function PawnFigure({
  side,
  crowned,
  immune,
  inscription,
  marked,
  flipRoute,
  children,
}: {
  side: Side;
  crowned: boolean;
  immune: boolean;
  inscription: Inscription | null;
  marked?: boolean;
  flipRoute?: boolean;
  /** Drawn on the body, floating with it (the Long Night's Carvings and seams). */
  children?: ReactNode;
}) {
  const theme = useTheme();
  const [name, plain, ascended] = SPRITES[theme === 'sea' ? 'sea' : 'void'][side];
  return (
    <span className={`pawn-figure pawn-${side} ${crowned ? 'is-ascended' : ''}`} aria-hidden="true">
      <SpriteSvg sprite={crowned ? SHADOW_WIDE : SHADOW} />
      <span className="pawn-float">
        <SpriteSvg sprite={crowned ? ascended : plain} name={crowned ? `${name}-crowned` : name} />
        {children}
      </span>
      {crowned && theme !== 'sea' && <Glints />}
      {immune && (
        <span className="pawn-badge pawn-immune">
          <svg viewBox="0 0 20 20" width="100%" height="100%">
            <path d="M10 1l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V4z" fill="currentColor" />
          </svg>
        </span>
      )}
      {inscription && (
        <span className="pawn-badge pawn-inscription">
          <RouteGlyph links={inscription.links} size={16} flip={flipRoute} />
        </span>
      )}
      {marked && <span className="pawn-ticket" />}
    </span>
  );
}

/** Two glints twinkling beside an ascended piece, each a layer of its own (styles.css). */
function Glints() {
  return (
    <>
      {[1, 2].map((n) => (
        <span key={n} className={`ascend-glint ascend-glint-${n}`}>
          <svg viewBox="0 0 5 5" shapeRendering="crispEdges">
            <path d="M2 0h1v5h-1zM0 2h5v1h-5z" />
          </svg>
        </span>
      ))}
    </>
  );
}
